import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../assets/icons/icons';
import { MONTHS_RU, WEEKDAYS_RU, buildMonthGrid, isSameDay } from './dateUtils';
import styles from './DatePicker.module.scss';

export interface CalendarMonthProps {
    month: Date;
    today: Date;
    rangeFrom: Date | null;
    rangeTo: Date | null;
    isDayDisabled: (day: Date) => boolean;
    onDayClick: (day: Date) => void;
    onDayHover?: (day: Date) => void;
    onPrev?: () => void;
    onNext?: () => void;
    canPrev?: boolean;
    canNext?: boolean;
}

export const CalendarMonth = ({
    month,
    today,
    rangeFrom,
    rangeTo,
    isDayDisabled,
    onDayClick,
    onDayHover,
    onPrev,
    onNext,
    canPrev = true,
    canNext = true,
}: CalendarMonthProps) => (
    <div className={styles.month}>
        <div className={styles.monthHeader}>
            {onPrev ? (
                <button
                    type="button"
                    className={styles.navButton}
                    onClick={onPrev}
                    disabled={!canPrev}
                    aria-label="Предыдущий месяц"
                >
                    <ChevronLeftIcon className={styles.icon} />
                </button>
            ) : (
                <span className={styles.navSpacer} />
            )}

            <span className={styles.monthTitle}>
                {MONTHS_RU[month.getMonth()]} {month.getFullYear()}
            </span>

            {onNext ? (
                <button
                    type="button"
                    className={styles.navButton}
                    onClick={onNext}
                    disabled={!canNext}
                    aria-label="Следующий месяц"
                >
                    <ChevronRightIcon className={styles.icon} />
                </button>
            ) : (
                <span className={styles.navSpacer} />
            )}
        </div>

        <div className={styles.weekdays}>
            {WEEKDAYS_RU.map((weekday) => (
                <span key={weekday}>{weekday}</span>
            ))}
        </div>

        <div className={styles.grid}>
            {buildMonthGrid(month).map((day) => {
                const isOutside = day.getMonth() !== month.getMonth();
                const inRange = Boolean(rangeFrom && rangeTo) && day >= (rangeFrom as Date) && day <= (rangeTo as Date);
                const isStart = Boolean(rangeFrom) && isSameDay(day, rangeFrom as Date);
                const isEnd = Boolean(rangeTo) && isSameDay(day, rangeTo as Date);

                const className = [
                    styles.day,
                    isOutside && styles.dayOutside,
                    inRange && styles.dayInRange,
                    (isStart || isEnd) && styles.dayEdge,
                    isStart && styles.dayStart,
                    isEnd && styles.dayEnd,
                    isSameDay(day, today) && styles.dayToday,
                ]
                    .filter(Boolean)
                    .join(' ');

                return (
                    <button
                        type="button"
                        key={day.toISOString()}
                        className={className}
                        disabled={isDayDisabled(day)}
                        onClick={() => onDayClick(day)}
                        onMouseEnter={onDayHover ? () => onDayHover(day) : undefined}
                        aria-label={day.toLocaleDateString('ru-RU')}
                        aria-pressed={isStart || isEnd}
                    >
                        {day.getDate()}
                    </button>
                );
            })}
        </div>
    </div>
);